import React, { useState } from "react";
import { publicationAPI } from "../Redux/services/PublicationService";
import { useNavigate } from "react-router-dom";

interface UserInfo {
    id: number;
    full_name: string;
    login: string;
    role: string;
    read_at: string | null;
    seen_at: string | null;
    time_to_read: string | null;
}

interface Publication {
    id: number;
    title: string;
    author: string;
    category: string;
    created_at: string;
    content: string;
    users: UserInfo[];
}

const CategoryControlPage: React.FC = () => {
    const [selectedPubId, setSelectedPubId] = useState<number | null>(null);
    const { data: publications, isLoading, isError } = publicationAPI.useFetchPublicationsWithUserQuery("");
    const navigate = useNavigate();
    const [deletePublication] = publicationAPI.useDeletePublicationMutation();

    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");

    // 🔍 Поиск по пользователям внутри выбранной публикации
    const [userSearch, setUserSearch] = useState("");

    const formatNegativeSeconds = (value: number | string) => {
        const sec = Math.abs(Number(value));

        const hours = Math.floor(sec / 3600);
        const minutes = Math.floor((sec % 3600) / 60);
        const remainingSec = sec % 60;

        return `${hours}:${minutes.toString().padStart(2, "0")}:${remainingSec
            .toString()
            .padStart(2, "0")}`;
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen text-gray-600">
                Завантаження...
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex items-center justify-center h-screen text-red-500">
                Помилка при завантаженні публікацій.
            </div>
        );
    }

    if (!publications || publications.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-screen text-gray-500">
                <p className="mb-4">Немає доступних публікацій.</p>
                <button
                    onClick={() => navigate("/createPost")}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                    Створити публікацію
                </button>
            </div>
        );
    }

    const categories = Array.from(new Set(publications.map((p: Publication) => p.category)));

    // 🔍 Фильтрация публикаций
    const filteredPublications = publications.filter((p: Publication) => {
        const matchesTitle = p.title.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = categoryFilter ? p.category === categoryFilter : true;
        return matchesTitle && matchesCategory;
    });

    const selectedPublication = filteredPublications.find((p: Publication) => p.id === selectedPubId) || null;

    // 🔍 Фильтрация пользователей по full_name
    const filteredUsers =
        selectedPublication?.users.filter((u:any) =>
            u.full_name.toLowerCase().includes(userSearch.toLowerCase())
        ) || [];

    const handleDelete = async (id: number) => {
        if (window.confirm("Ви впевнені, що хочете видалити цю публікацію?")) {
            await deletePublication(id);
            setSelectedPubId(null);
        }
    };

    const handleEdit = (id: number) => {
        navigate(`/editPost/${id}`);
    };

    return (
        <div className="flex flex-col md:flex-row bg-gray-100 h-screen md:h-[70vh] overflow-y-auto">

            {/* 📍 Левый блок */}
            {(!selectedPubId || window.innerWidth >= 768) && (
                <div className="w-full md:w-1/3 bg-white border-r border-gray-300 flex flex-col">
                    <div className="flex justify-between items-center p-3 md:p-4 border-b border-gray-300">
                        <h2 className="text-lg md:text-xl font-semibold">Публікації</h2>
                        <button
                            onClick={() => navigate("/createPost")}
                            className="px-2 md:px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition text-sm"
                        >
                            Створити
                        </button>
                    </div>

                    {/* 🔍 filters */}
                    <div className="p-3 md:p-4 border-b border-gray-200 flex flex-col gap-2">
                        <input
                            type="text"
                            placeholder="Пошук за назвою..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-400 text-sm"
                        />
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-400 text-sm"
                        >
                            <option value="">Всі категорії</option>
                            {categories.map((cat:any) => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* 📄 список публикаций */}
                    <div className="flex-1 overflow-y-auto">
                        {categories.map((category:any) => {
                            const categoryPublications = filteredPublications.filter(
                                (p:any) => p.category === category
                            );
                            if (categoryPublications.length === 0) return null;

                            return (
                                <div key={category} className="border-b border-gray-200">
                                    <h3 className="text-base md:text-lg font-medium px-4 py-2 bg-gray-50 text-gray-700">
                                        {category}
                                    </h3>
                                    <ul>
                                        {categoryPublications.map((pub:any) => (
                                            <li
                                                key={pub.id}
                                                className={`p-3 md:p-4 cursor-pointer hover:bg-gray-100 ${
                                                    selectedPubId === pub.id ? "bg-gray-200" : ""
                                                }`}
                                                onClick={() => {
                                                    setUserSearch("");
                                                    setSelectedPubId(pub.id);
                                                }}
                                            >
                                                <p className="font-semibold text-sm md:text-base">{pub.title}</p>
                                                <p className="text-xs md:text-sm text-gray-500">Автор: {pub.author}</p>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* 📍 Правый блок */}
            {(selectedPubId || window.innerWidth >= 768) && (
                <div className="flex-1 p-3 md:p-6 overflow-y-auto">

                    {selectedPubId && window.innerWidth < 768 && (
                        <button
                            onClick={() => setSelectedPubId(null)}
                            className="text-2xl mb-3"
                        >
                            ←
                        </button>
                    )}

                    {selectedPublication ? (
                        <>
                            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-3">
                                <div>
                                    <h2 className="text-xl md:text-2xl font-semibold">{selectedPublication.title}</h2>
                                    <p className="text-gray-600 text-sm md:text-base">
                                        Категорія: <span className="font-medium">{selectedPublication.category}</span> • Автор:{" "}
                                        <span className="font-medium">{selectedPublication.author}</span>
                                    </p>
                                </div>

                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleEdit(selectedPublication.id)}
                                        className="px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                                    >
                                        Редагувати
                                    </button>
                                    <button
                                        onClick={() => handleDelete(selectedPublication.id)}
                                        className="px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
                                    >
                                        Видалити
                                    </button>
                                </div>
                            </div>

                            {/* 🔍 Поиск по пользователям */}
                            {selectedPublication.users.length > 0 && (
                                <input
                                    type="text"
                                    placeholder="Пошук користувача..."
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                    className="w-full mb-4 px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-400"
                                />
                            )}

                            {/* 🧍‍♂️ Users */}
                            {filteredUsers.length > 0 ? (
                                <div className="space-y-3">
                                    {filteredUsers.map((user:any) => (
                                        <div
                                            key={user.id}
                                            className="p-3 md:p-4 bg-white rounded-lg shadow flex justify-between items-center"
                                        >
                                            <div>
                                                <p className="font-semibold text-sm md:text-base">{user.full_name}</p>
                                                <p className="text-xs md:text-sm text-gray-500">{user.role}</p>
                                            </div>
                                            <div className="text-xs md:text-sm text-right">
                                                <p><span className="font-medium">Прочитав:</span> {user.read_at || "❌ Ні"}</p>
                                                <p><span className="font-medium">Перегляд:</span> {user.seen_at || "—"}</p>
                                                <p>
                                                    <span className="font-medium">Час читання:</span>{" "}
                                                    {user.time_to_read ? formatNegativeSeconds(user.time_to_read) : "—"}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500">
                                    {userSearch.length > 0
                                        ? "Користувачів не знайдено."
                                        : "Немає користувачів, пов'язаних із цією публікацією."}
                                </p>
                            )}
                        </>
                    ) : (
                        <p className="text-gray-500 text-center md:text-left mt-6 md:mt-0">
                            Оберіть публікацію зі списку.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default CategoryControlPage;
